import { FundClassification } from '@/models/common/FundClassification';
import { FundGoals } from '@/models/common/FundGoals';
import { RelationshipOptions } from '@/models/common/RelationshipOptions';
import type { OpenFundRequestDtoOut } from '@/models/out/OpenFundRequestDtoOut';
import type {
  CoordinatorFormData,
  OpenFundFormData,
  SupportedFormData,
} from '../schemas/openFundFormSchema';
import { toLocalPhone } from '@/helpers/phoneHelper';
import { interpretWelfareCoordinators } from './welfareCoordinators';

// Documents (ID cards, appendices) are preserved server-side and are not re-collected on completion.
// The signature is re-captured. All text/select/flag fields are prefilled.
const mapCoordinator = (c: OpenFundRequestDtoOut['coordinators'][number]): CoordinatorFormData => ({
  firstName: c.firstName ?? '',
  lastName: c.lastName ?? '',
  israeliId: c.israeliId ?? '',
  city: c.city ?? '',
  street: c.street ?? '',
  houseNumber: c.houseNumber ?? '',
  email: c.email ?? '',
  phone: toLocalPhone(c.phone),
  relationToSupported: c.relationToSupported ?? '',
  idCardPhotocopy: null as unknown as File,
  idAppendixPhotocopy: null as unknown as File,
  additionalDocuments: [],
  spouseFirstName: c.spouseFirstName ?? '',
  spouseIsraeliId: c.spouseIsraeliId ?? '',
});

const mapSupported = (s: OpenFundRequestDtoOut['supporteds'][number]): SupportedFormData => ({
  firstName: s.firstName ?? '',
  lastName: s.lastName ?? '',
  israeliId: s.israeliId ?? '',
  city: s.city ?? '',
  street: s.street ?? '',
  houseNumber: s.houseNumber ?? '',
  email: s.email ?? '',
  phone: toLocalPhone(s.phone),
  idCardPhotocopy: null as unknown as File,
  idAppendixPhotocopy: null as unknown as File,
  additionalDocuments: [],
  relationshipToFirstSupported: s.relationshipToFirstSupported ?? RelationshipOptions.Self,
});

export const mapRequestToFormData = (request: OpenFundRequestDtoOut): OpenFundFormData => {
  const isWelfare =
    request.fundClassification === FundClassification.Welfare ||
    request.fundClassification === FundClassification.CampaignForWelfare;

  const common = {
    fundName: request.fundName ?? '',
    reasonSummary: request.reasonSummary ?? '',
    personalInterfaceInNedarimPlus: request.personalInterfaceInNedarimPlus ?? false,
    crowdfundingCampaign: request.crowdfundingCampaign ?? false,
    // Terms, regulations and signature must be re-confirmed/re-signed on resubmission.
    termsAndConditionsAccepted: false,
    regulationsAccepted: false,
    signature: '',
    fundGoals: (request.fundGoals ?? []) as FundGoals[],
    otherGoalDescription: request.otherGoalDescription ?? '',
  };

  if (isWelfare) {
    const { isCoordinator, visibleCoordinators } = interpretWelfareCoordinators(request);

    return {
      ...common,
      fundClassification: FundClassification.Welfare,
      supporteds: (request.supporteds ?? []).map(mapSupported),
      isCoordinator,
      supportedAwareOfFund: request.supportedAwareOfFund ?? true,
      supportedCanViewFund: request.supportedCanViewFund ?? false,
      coordinators: visibleCoordinators.map(mapCoordinator),
    } as OpenFundFormData;
  }

  return {
    ...common,
    fundClassification: FundClassification.Community,
    responsibleParty: (request.responsibleParty as 'community' | 'charityTrustee') ?? 'community',
    responsibleEntityName: request.responsibleEntityName ?? '',
    coordinators: (request.coordinators ?? []).map(mapCoordinator),
  } as OpenFundFormData;
};
